import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import CircularProgress from '@mui/material/CircularProgress';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { OrgProvider, useActiveOrg } from './contexts/OrgContext';
import axios from 'axios';
import { API_BASE_URL, PRICING_ENABLED } from './utils';

// --- Eagerly loaded shell: needed for the very first paint and for the routing
//     decisions in RootRoute/PrivateRoute, so they stay in the main bundle. ---
import Layout from './components/Layout';
import RoleChooser from './components/RoleChooser';
import PanelSelector from './components/PanelSelector';
import LandingPage from './pages/LandingPage'; // public home — eager for a fast LCP

// --- Route pages: lazily loaded so each becomes its own chunk. The initial bundle
//     no longer ships the admin panel, MUI data-grid, charts or Mermaid (~1 MB) —
//     a route only downloads its JS the first time it's visited. ---
const OrganizationsList = lazy(() => import('./pages/organization/OrganizationsList'));
const OrganizationDetail = lazy(() => import('./pages/organization/OrganizationDetail'));
const CreateOrganization = lazy(() => import('./pages/organization/CreateOrganization'));
const EditOrganization = lazy(() => import('./pages/organization/EditOrganization'));
const AccountsList = lazy(() => import('./pages/account/AccountsList'));
const CreateAccount = lazy(() => import('./pages/account/CreateAccount'));
const AccountDetail = lazy(() => import('./pages/account/AccountDetail'));
const MyBusiness = lazy(() => import('./pages/account/MyBusiness'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const AcceptInvite = lazy(() => import('./pages/AcceptInvite'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const PurposeManagement = lazy(() => import('./pages/consent/PurposeManagement'));
const NoticeBuilder = lazy(() => import('./pages/consent/NoticeBuilder'));
const ConsentLogs = lazy(() => import('./pages/consent/ConsentLogs'));
const CommunicationsLaunch = lazy(() => import('./pages/CommunicationsLaunch'));
const ConsentExpiry = lazy(() => import('./pages/consent/ConsentExpiry'));
const UserManagement = lazy(() => import('./pages/organization/UserManagement'));
const AuditLogs = lazy(() => import('./pages/organization/AuditLogs'));
const Documentation = lazy(() => import('./pages/Documentation'));
const Support = lazy(() => import('./pages/Support'));
const SupportInbox = lazy(() => import('./pages/SupportInbox'));
const PlatformAdmins = lazy(() => import('./pages/PlatformAdmins'));
const AdminActivity = lazy(() => import('./pages/AdminActivity'));
const UsageMonitor = lazy(() => import('./pages/UsageMonitor'));
const FaqManager = lazy(() => import('./pages/FaqManager'));
const AnnouncementManager = lazy(() => import('./pages/content/AnnouncementManager'));
const ChecklistManager = lazy(() => import('./pages/content/ChecklistManager'));
const TestimonialManager = lazy(() => import('./pages/content/TestimonialManager'));
const LegalPagesManager = lazy(() => import('./pages/content/LegalPagesManager'));
const LegalPage = lazy(() => import('./pages/LegalPage'));
const Profile = lazy(() => import('./pages/Profile'));
const AccountSettings = lazy(() => import('./pages/AccountSettings'));
const DocumentationAPI = lazy(() => import('./pages/DocumentationAPI'));
const CookieConsentBuilder = lazy(() => import('./pages/consent/CookieConsentBuilder'));
const ComingSoon = lazy(() => import('./pages/ComingSoon'));
const RoleList = lazy(() => import('./pages/organization/RoleList'));
const RoleCreate = lazy(() => import('./pages/organization/RoleCreate'));
const CookieMetrics = lazy(() => import('./pages/consent/CookieMetrics'));
const TechnicalOverview = lazy(() => import('./pages/TechnicalOverview'));
const EmbedFrame = lazy(() => import('./pages/EmbedFrame'));
const DpdpCalculatorPage = lazy(() => import('./pages/DpdpCalculatorPage'));
const DpdpChecklistPage = lazy(() => import('./pages/DpdpChecklistPage'));
const DpdpFaqPage = lazy(() => import('./pages/DpdpFaqPage'));
const BillingPage = lazy(() => import('./pages/billing/BillingPage'));
const BillingAdminPage = lazy(() => import('./pages/billing/BillingAdminPage'));
const CisoFrame = lazy(() => import('./pages/CisoFrame'));
const OpenMetadataFrame = lazy(() => import('./pages/OpenMetadataFrame'));
// Data Principal Rights (DPDP) — public portal + staff console.
const DprPortal = lazy(() => import('./pages/dpr/DprPortal'));
const DprVerify = lazy(() => import('./pages/dpr/DprVerify'));
const DprStatus = lazy(() => import('./pages/dpr/DprStatus'));
const DataPrincipalRequests = lazy(() => import('./pages/dpr/DataPrincipalRequests'));
const DprRequestDetail = lazy(() => import('./pages/dpr/DprRequestDetail'));
const DprApiKeys = lazy(() => import('./pages/dpr/DprApiKeys'));
const DownstreamSystems = lazy(() => import('./pages/dpr/DownstreamSystems'));

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#FF6600', // CarDekho Orange
      light: '#FF8533',
      dark: '#E55A00',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#2C3E50', // Dark blue-gray for contrast
      light: '#34495E',
      dark: '#1A252F',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#F8F9FA',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#2C3E50',
      secondary: '#6C757D',
    },
    success: {
      main: '#28A745',
      light: '#34CE57',
      dark: '#1E7E34',
    },
    warning: {
      main: '#FFC107',
      light: '#FFD54F',
      dark: '#FF8F00',
    },
    error: {
      main: '#DC3545',
      light: '#E57373',
      dark: '#C62828',
    },
    info: {
      main: '#17A2B8',
      light: '#4FC3F7',
      dark: '#0277BD',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 600,
      fontSize: '2.5rem',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.43,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '8px 16px',
          fontWeight: 600,
          fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        },
        contained: {
          boxShadow: '0 2px 8px rgba(255, 102, 0, 0.2)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(255, 102, 0, 0.3)',
          },
        },
      },
    },
    // --- Consistent form fields everywhere (incl. all add/create popups) ---
    // Force a single variant for every text field & dropdown so modals don't mix
    // outlined inputs with borderless/card-style ones.
    MuiTextField: { defaultProps: { variant: 'outlined' } },
    MuiSelect: { defaultProps: { variant: 'outlined' } },
    MuiFormControl: { defaultProps: { variant: 'outlined' } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: '#FFFFFF',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E6EAF0' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#FF6600' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FF6600', borderWidth: 1 },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { '&.Mui-focused': { color: '#C2410C' } },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(255, 102, 0, 0.08)',
          border: '1px solid rgba(255, 102, 0, 0.1)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    // --- Modern SaaS popups (applies to every Dialog system-wide) ---
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          border: '1px solid #E6EAF0',
          boxShadow: '0 24px 48px -12px rgba(16,24,40,0.20), 0 8px 16px -8px rgba(16,24,40,0.10)',
          backgroundImage: 'none',
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        // Dim + blur the page behind modals to focus the popup.
        root: {
          backgroundColor: 'rgba(15,23,42,0.45)',
          backdropFilter: 'blur(4px)',
        },
        // Menus / Selects / Popovers use an invisible backdrop — keep it clear
        // so dropdowns don't blur the whole screen.
        invisible: {
          backgroundColor: 'transparent',
          backdropFilter: 'none',
        },
      },
    },
    // --- Dropdown menus (Select / Menu / autocomplete) ---
    // Must be FULLY opaque and clearly elevated above page content — otherwise
    // the options render semi-transparent and bleed into the fields behind them.
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FFFFFF',
          backgroundImage: 'none',
          border: '1px solid #E6EAF0',
          boxShadow: '0 8px 24px rgba(16,24,40,0.16)',
        },
        list: { backgroundColor: '#FFFFFF', paddingTop: 4, paddingBottom: 4 },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: { backgroundColor: '#FFFFFF', backgroundImage: 'none' },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
          fontSize: '1.15rem',
          fontWeight: 800,
          color: '#0F172A',
          letterSpacing: '-0.01em',
          padding: '20px 24px 12px',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '8px 24px 20px',
        },
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: {
          color: '#64748B',
          fontSize: '0.9rem',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '12px 24px 20px',
          gap: 8,
        },
      },
    },
  },
});

const PrivateRoute = ({ children, pageTitle }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </div>
    );
  }
  return user ? <Layout pageTitle={pageTitle}>{children}</Layout> : <Navigate to="/login" />;
};

// Public root: marketing landing page for anonymous visitors; authenticated
// users (including super admins) land on the dashboard.
// Ordered landing candidates for a business user: the first page they hold the
// permission for becomes their home. Lets a restricted RBAC role (e.g. one
// without dashboard:view) land somewhere it can actually use.
const LANDING_ROUTES = [
  { path: '/dashboard', perm: 'dashboard:view' },
  { path: '/consent-management/purposes', perm: 'purposes:view' },
  { path: '/consent-management/notices', perm: 'notices:view' },
  { path: '/consent-management/logs', perm: 'consents:view' },
  { path: '/cookie-management/cookie-consent', perm: 'cookies:view' },
  { path: '/organization/audit-logs', perm: 'audit_logs:view' },
  { path: '/organization/users', perm: 'users:view' },
  { path: '/organization/roles', perm: 'roles:view' },
  { path: '/billing', perm: 'billing:view' },
  { path: '/my-business', perm: 'account:view' },
];
const resolveLanding = (hasPermission) => {
  const hit = LANDING_ROUTES.find((r) => hasPermission(r.perm));
  return hit ? hit.path : '/profile'; // profile is always reachable as a last resort
};

const RootRoute = () => {
  const { user, loading, isPlatformAdmin, isDualAccess, roles, activeRoleId, hasPermission } = useAuth();
  const { activeOrgId } = useActiveOrg();
  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </div>
    );
  }
  if (!user) return <LandingPage />;

  // Dual-access user (platform admin + account admin): pick a panel first so the
  // two workspaces don't mix. The choice is remembered for the session.
  if (isDualAccess) {
    const mode = sessionStorage.getItem('panelMode');
    if (mode === 'platform') return <Navigate to="/admin-panel" replace />;
    if (mode === 'business') return <Navigate to={resolveLanding(hasPermission)} replace />;
    return <PanelSelector />;
  }

  // Platform admins (super admin + scoped Billing/Org/Auditor roles) are
  // platform-only — the Admin Panel is their home.
  if (isPlatformAdmin) return <Navigate to="/admin-panel" replace />;
  // A user with more than one role picks which to act as before proceeding.
  if ((roles?.length || 0) > 1 && !activeRoleId) return <RoleChooser />;
  // Land on the first page this user's permissions allow (not always /dashboard).
  if (activeOrgId) return <Navigate to={resolveLanding(hasPermission)} replace />;
  // No active org in the token: a user who actually HAS organizations (e.g. an
  // account admin whose token predates org-resolution) gets auto-switched into
  // their first one; only a genuinely org-less user goes to create-org.
  return <NoOrgLanding />;
};

// Resolves the landing page when context shows no active_org_id. Decides off a
// DIRECT fetch (not lagging context state) to avoid a race where the freshly
// minted token hasn't been re-read yet and we wrongly bounce to create-org.
const NoOrgLanding = () => {
  const { switchOrg } = useActiveOrg();
  const [decision, setDecision] = React.useState(null); // null = deciding, 'create' = no orgs

  React.useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE_URL}/api/organizations/switchable`)
      .then((r) => {
        if (cancelled) return;
        // The endpoint echoes the JWT's resolved active_org_id — if present, the
        // token is fine and we just lost a render race; go straight to dashboard.
        if (r.data?.active_org_id) { window.location.href = '/dashboard'; return; }
        const list = r.data?.organizations || [];
        if (list.length > 0) {
          switchOrg(list[0].id)
            .then(() => { window.location.href = '/dashboard'; })
            .catch(() => { window.location.href = '/dashboard'; });
        } else {
          setDecision('create');
        }
      })
      .catch(() => { if (!cancelled) setDecision('create'); });
    return () => { cancelled = true; };
  }, [switchOrg]);

  if (decision === 'create') return <Navigate to="/organizations/new" replace />;
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <CircularProgress />
    </div>
  );
};

// Wrapper that keys embedded iframes on activeOrgId. Any time a super_admin
// switches tenants, the inner React.Fragment re-keys, so children unmount and
// remount — evicting the per-tenant localStorage/IndexedDB state inside CISO,
// OpenMetadata, and Data Bunker iframes.
const KeyedByOrg = ({ children }) => {
  const { activeOrgId } = useActiveOrg();
  return <React.Fragment key={activeOrgId || 'no-org'}>{children}</React.Fragment>;
};

// Shown while a lazily-loaded route chunk is being fetched.
const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <CircularProgress />
  </div>
);

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <OrgProvider>
        <Router>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public routes */}
            <Route path="/welcome" element={<LandingPage />} />
            <Route path="/dpdp-penalty-calculator" element={<DpdpCalculatorPage />} />
            <Route path="/dpdp-readiness-checklist" element={<DpdpChecklistPage />} />
            <Route path="/dpdp-faq" element={<DpdpFaqPage />} />
            <Route path="/legal/:slug" element={<LegalPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            {PRICING_ENABLED && <Route path="/pricing" element={<PricingPage />} />}
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route path="/set-password" element={<AcceptInvite />} />

            {/* Data Principal Rights — public portal (unauthenticated) */}
            <Route path="/dpr" element={<DprPortal />} />
            <Route path="/dpr/verify" element={<DprVerify />} />
            <Route path="/dpr/track" element={<DprStatus />} />

            {/* Public root — landing page (or dashboard if signed in) */}
            <Route path="/" element={<RootRoute />} />

            {/* Protected routes */}
            <Route
              path="/dashboard"
              element={
                <PrivateRoute pageTitle="Dashboard">
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/user-journey"
              element={
                <PrivateRoute pageTitle="User Journey">
                  <ComingSoon />
                </PrivateRoute>
              }
            />
            {/* Consent Management Routes */}
            <Route
              path="/consent-management/purposes"
              element={
                <PrivateRoute pageTitle="Purpose Management">
                  <PurposeManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/consent-management/notices"
              element={
                <PrivateRoute pageTitle="Notice Builder">
                  <NoticeBuilder />
                </PrivateRoute>
              }
            />
            <Route
              path="/consent-management/logs"
              element={
                <PrivateRoute pageTitle="Consent Logs">
                  <ConsentLogs />
                </PrivateRoute>
              }
            />
            <Route
              path="/communications"
              element={
                <PrivateRoute pageTitle="Communications">
                  <CommunicationsLaunch />
                </PrivateRoute>
              }
            />
            {/* Data Principal Rights — staff console */}
            <Route
              path="/data-principal-rights"
              element={
                <PrivateRoute pageTitle="Data Principal Rights">
                  <DataPrincipalRequests />
                </PrivateRoute>
              }
            />
            <Route
              path="/data-principal-rights/api-keys"
              element={
                <PrivateRoute pageTitle="Portal API Keys">
                  <DprApiKeys />
                </PrivateRoute>
              }
            />
            <Route
              path="/data-principal-rights/downstream-systems"
              element={
                <PrivateRoute pageTitle="Downstream Systems">
                  <DownstreamSystems />
                </PrivateRoute>
              }
            />
            <Route
              path="/data-principal-rights/:id"
              element={
                <PrivateRoute pageTitle="Data Principal Request">
                  <DprRequestDetail />
                </PrivateRoute>
              }
            />
            {/* Billing & Invoicing */}
            <Route
              path="/billing"
              element={
                <PrivateRoute pageTitle="Billing">
                  <BillingPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/billing-admin"
              element={
                <PrivateRoute pageTitle="Billing Administration">
                  <BillingAdminPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/consent-management/expiry"
              element={
                <PrivateRoute pageTitle="Consent Expiry">
                  <ConsentExpiry />
                </PrivateRoute>
              }
            />
            <Route
              path="/cookie-management/cookie-consent"
              element={
                <PrivateRoute pageTitle="Cookie Consent Builder">
                  <CookieConsentBuilder />
                </PrivateRoute>
              }
            />
            <Route
              path="/cookie-management"
              element={
                <PrivateRoute pageTitle="Cookie Management">
                  <ComingSoon />
                </PrivateRoute>
              }
            />
            <Route
              path="/data-sources"
              element={
                <PrivateRoute pageTitle="Data Sources">
                  <ComingSoon />
                </PrivateRoute>
              }
            />
            <Route
              path="/vault"
              element={
                <PrivateRoute pageTitle="Vault">
                  <ComingSoon />
                </PrivateRoute>
              }
            />
            <Route
              path="/tprm"
              element={
                <PrivateRoute pageTitle="TPRM">
                  <ComingSoon />
                </PrivateRoute>
              }
            />
            <Route
              path="/dpr-handling"
              element={
                <PrivateRoute pageTitle="DPR Handling">
                  <ComingSoon />
                </PrivateRoute>
              }
            />
            <Route
              path="/trust-center"
              element={
                <PrivateRoute pageTitle="Trust Center">
                  <ComingSoon />
                </PrivateRoute>
              }
            />
            <Route
              path="/organization/users"
              element={
                <PrivateRoute pageTitle="User Management">
                  <UserManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/organization/roles"
              element={
                <PrivateRoute pageTitle="Role Management">
                  <RoleList />
                </PrivateRoute>
              }
            />
            <Route
              path="/organization/roles/create"
              element={
                <PrivateRoute pageTitle="Create Role">
                  <RoleCreate />
                </PrivateRoute>
              }
            />
            <Route
              path="/organization/roles/edit/:id"
              element={
                <PrivateRoute pageTitle="Edit Role">
                  <RoleCreate />
                </PrivateRoute>
              }
            />
            <Route
              path="/organization/audit-logs"
              element={
                <PrivateRoute pageTitle="Audit Logs">
                  <AuditLogs />
                </PrivateRoute>
              }
            />
            <Route
              path="/organization/settings"
              element={
                <PrivateRoute pageTitle="Organization Settings">
                  <ComingSoon />
                </PrivateRoute>
              }
            />
            <Route
              path="/documentation"
              element={
                <PrivateRoute pageTitle="Documentation">
                  <Documentation />
                </PrivateRoute>
              }
            />
            <Route
              path="/documentation/api"
              element={
                <PrivateRoute pageTitle="API Documentation">
                  <DocumentationAPI />
                </PrivateRoute>
              }
            />
            <Route
              path="/documentation/technical"
              element={
                <PrivateRoute pageTitle="Technical Overview">
                  <TechnicalOverview />
                </PrivateRoute>
              }
            />
            <Route
              path="/support"
              element={
                <PrivateRoute pageTitle="Support">
                  <Support />
                </PrivateRoute>
              }
            />
            <Route
              path="/support-inbox"
              element={
                <PrivateRoute pageTitle="Support Inbox">
                  <SupportInbox />
                </PrivateRoute>
              }
            />
            <Route
              path="/platform-admins"
              element={
                <PrivateRoute pageTitle="Platform Admins">
                  <PlatformAdmins />
                </PrivateRoute>
              }
            />
            <Route
              path="/faq-manager"
              element={
                <PrivateRoute pageTitle="FAQ Manager">
                  <FaqManager />
                </PrivateRoute>
              }
            />
            <Route
              path="/content/announcements"
              element={
                <PrivateRoute pageTitle="Announcements">
                  <AnnouncementManager />
                </PrivateRoute>
              }
            />
            <Route
              path="/content/checklist"
              element={
                <PrivateRoute pageTitle="Compliance Checklist">
                  <ChecklistManager />
                </PrivateRoute>
              }
            />
            <Route
              path="/content/testimonials"
              element={
                <PrivateRoute pageTitle="Testimonials">
                  <TestimonialManager />
                </PrivateRoute>
              }
            />
            <Route
              path="/content/legal"
              element={
                <PrivateRoute pageTitle="Legal Pages">
                  <LegalPagesManager />
                </PrivateRoute>
              }
            />
            <Route
              path="/usage-monitor"
              element={
                <PrivateRoute pageTitle="Usage Monitoring">
                  <UsageMonitor />
                </PrivateRoute>
              }
              />

            <Route
              path="/admin/activity"
              element={
                <PrivateRoute pageTitle="Admin Activity">
                  <AdminActivity />
                </PrivateRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <PrivateRoute pageTitle="My Profile">
                  <Profile />
                </PrivateRoute>
              }
            />
            <Route
              path="/account-settings"
              element={
                <PrivateRoute pageTitle="Account Settings">
                  <AccountSettings />
                </PrivateRoute>
              }
            />
            <Route
              path="/consent/cookie-metrics"
              element={
                <PrivateRoute pageTitle="Cookie Metrics">
                  <CookieMetrics />
                </PrivateRoute>
              }
            />

            {/* External tool embeds — keyed by active org so a super_admin
                switch fully remounts the iframe and its per-tenant cache. */}
            <Route
              path="/ciso-assistant/*"
              element={
                <PrivateRoute pageTitle="CISO Assistant">
                  <KeyedByOrg><CisoFrame /></KeyedByOrg>
                </PrivateRoute>
              }
            />
            <Route
              path="/open-metadata/*"
              element={
                <PrivateRoute pageTitle="Open Metadata">
                  <KeyedByOrg><OpenMetadataFrame /></KeyedByOrg>
                </PrivateRoute>
              }
            />
            <Route
              path="/data-bunker"
              element={
                <PrivateRoute pageTitle="Data Bunker">
                  <KeyedByOrg>
                    <EmbedFrame url={process.env.REACT_APP_DATABUNKER_URL || 'http://localhost:3000'} title="Data Bunker" />
                  </KeyedByOrg>
                </PrivateRoute>
              }
            />

            {/* Super-admin control center */}
            <Route
              path="/admin-panel"
              element={
                <PrivateRoute pageTitle="Admin Panel">
                  <AdminPanel />
                </PrivateRoute>
              }
            />

            {/* Account-admin self-service (their own business) */}
            <Route
              path="/my-business"
              element={
                <PrivateRoute pageTitle="My Business">
                  <MyBusiness />
                </PrivateRoute>
              }
            />

            {/* Super-admin business (account) control plane */}
            <Route
              path="/accounts"
              element={
                <PrivateRoute pageTitle="Business Accounts">
                  <AccountsList />
                </PrivateRoute>
              }
            />
            <Route
              path="/accounts/new"
              element={
                <PrivateRoute pageTitle="Create Account">
                  <CreateAccount />
                </PrivateRoute>
              }
            />
            <Route
              path="/accounts/:id"
              element={
                <PrivateRoute pageTitle="Account">
                  <AccountDetail />
                </PrivateRoute>
              }
            />

            {/* Super-admin tenant control plane */}
            <Route
              path="/organizations"
              element={
                <PrivateRoute pageTitle="Organizations">
                  <OrganizationsList />
                </PrivateRoute>
              }
            />
            <Route
              path="/organizations/new"
              element={
                <PrivateRoute pageTitle="Create Organization">
                  <CreateOrganization />
                </PrivateRoute>
              }
            />
            <Route
              path="/organizations/:id"
              element={
                <PrivateRoute pageTitle="Organization">
                  <OrganizationDetail />
                </PrivateRoute>
              }
            />
            <Route
              path="/organizations/:id/edit"
              element={
                <PrivateRoute pageTitle="Edit Organization">
                  <EditOrganization />
                </PrivateRoute>
              }
            />

            {/* Catch all route - keep the sidebar/Layout so users aren't stranded */}
            <Route path="*" element={<PrivateRoute pageTitle="Coming Soon"><ComingSoon /></PrivateRoute>} />
          </Routes>
          </Suspense>
        </Router>
        </OrgProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App; 